import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

// Components
import SystemsGrid from '../../Systems/SystemsGrid';
import MlbStatAvgs from './MlbStatAvgs';
import NflStatAvgs from './NflStatAvgs';
import NbaStatAvgs from './NbaStatAvgs';
import MlbBettingData from './MlbBettingData';
import SportBettingData from './SportBettingData';
import AuthProtected from '../../HOCs/AuthProtected';
import WeatherData from '../../Assets/gameComponents/weatherData';
import TeamBreakdown from '../../Assets/gameComponents/TeamBreakdown';
import LinesOverview from '../../Assets/gameComponents/LinesOverview';
import { PrimaryBtn } from '../../Assets/Buttons';

// Utils
import { nflTeams } from '../../../utils/nflUtils';
import { nbaTeams } from '../../../utils/nbaUtils';
import { mlbTeams } from '../../../utils/mlbUtils';
import { formatDatetimeString } from '../../../utils/general';

// Icons
import { StatsIcon, BettingIcon, SystemsIcon } from '../../common/icons';
import { ArrowLeftCircleIcon } from '@heroicons/react/24/outline'

const UpcomingGame = (props) => {
    // State
    const [activeTab, setActiveTab] = useState('overview');
    // Hooks
    const defaultLocation = useLocation();
    const defaultNavigate = useNavigate();
    const defaultParams = useParams();
    // Allow for both standalone page and embedded component use
    const location = props.location || defaultLocation;
    const navigate = props.navigate || defaultNavigate;
    const params = props.params || defaultParams;

    // Redux
    const sportFollowedSystems = useSelector(state => {
        switch(props?.sport || location.state?.sport){
            case "mlb":
                return state.user.followedSystems.mlb;
            case "nfl":
                return state.user.followedSystems.nfl;
            case "nba":
                return state.user.followedSystems.nba;
            default:
                return [];
        }
    });
    const sportStandings = useSelector((state) => {
        switch(location?.state?.sport){
            case "mlb":
                return state.mlb.standings;
            case "nfl":
                return state.nfl.standings;
            case "nba":
                return state.nba.standings;
            default:
                return null;
        }
    });
    const mlbStadiumOrientations = useSelector((state) => 
        props.sport === "mlb" ? state.mlb.stadiumOrientations : null
    );
    const matches = useSelector((state) => state.user.matches);
    const privateSystems = useSelector((state) => state.user.privateSystems);
    


    // Update scrollbar visibility - moved before any early returns
    useEffect(() => {
        // Only run if game and sport are available
        if (!props.game && !location.state?.game) return;
        if (!props.sport && !location.state?.sport) return;
        
        function updateScrollbarVisibility() {
            const tableContainers = document.querySelectorAll('.stats-table-container, .betting-table-container');
            
            tableContainers.forEach(container => {
                const tbody = container.querySelector('tbody');
                const thead = container.querySelector('thead');
                
                if (!tbody || !thead) return;
                
                // Check if scrollbar is visible
                const hasScrollbar = tbody.scrollHeight > tbody.clientHeight;
                
                // Update class on tbody
                if (hasScrollbar) {
                    tbody.classList.add('has-scrollbar');
                    container.classList.add('has-scrollbar');
                } else {
                    tbody.classList.remove('has-scrollbar');
                    container.classList.remove('has-scrollbar');
                }
                
                // Adjust thead width if scrollbar is present
                if (hasScrollbar) {
                    thead.style.width = 'calc(100% - 6px)';
                } else {
                    thead.style.width = '100%';
                }
            });
        }
        
        // Call immediately and on window resize
        updateScrollbarVisibility();
        window.addEventListener('resize', updateScrollbarVisibility);
        
        // Also call when tab changes since content may load dynamically
        const tabContentObserver = new MutationObserver(updateScrollbarVisibility);
        const tabContent = document.querySelector('.tab-content');
        if (tabContent) {
            tabContentObserver.observe(tabContent, { childList: true, subtree: true });
        }
        
        return () => {
            window.removeEventListener('resize', updateScrollbarVisibility);
            if (tabContentObserver) {
                tabContentObserver.disconnect();
            }
        };
    }, [activeTab, props.game, props.sport, location.state?.game, location.state?.sport]);

    let date = '';
    let time = '';

    // Extract Game and Sport - Handle both direct props and location state
    const game = props.game || location.state?.game;
    const sport = props.sport || location.state?.sport;
    
    // Early return if game or sport is not available
    if (!game || !sport) {
        return <div className="text-gray-500 dark:text-gray-400">Game data not available</div>;
    }


    let game_id = params?.id || game._id;

    // format date and time
    let datetime_obj = formatDatetimeString(game.date);
    date = datetime_obj.date;
    time = datetime_obj.time;


    let home_team, away_team, homeTeamLabel, awayTeamLabel, sportLabel = '';
    let followedSystems = [];
    // set team names and sport label and followed systems
    if(["mlb", "nfl", "nba"].includes(sport)){
        followedSystems = sportFollowedSystems;
        sportLabel = sport.toUpperCase();
        if(sport === "mlb"){
            homeTeamLabel = mlbTeams[game.home.team];
            awayTeamLabel = mlbTeams[game.away.team];
            home_team = game.home.team;
            away_team = game.away.team;
        }else if(sport === "nfl"){
            homeTeamLabel = nflTeams[game.home.team_id];
            awayTeamLabel = nflTeams[game.away.team_id];
            home_team = nflTeams[game.home.team_id];
            away_team = nflTeams[game.away.team_id];
        }else if(sport === "nba"){
            homeTeamLabel = nbaTeams[game.home.team_id];
            awayTeamLabel = nbaTeams[game.away.team_id];
            home_team = nbaTeams[game.home.team_id];
            away_team = nbaTeams[game.away.team_id];
        }
    }
    
    const game_matches = matches.filter(match => match.game_id === game_id);
    const priv_matches = privateSystems.filter(system =>
        game_matches.some(game_match => game_match.system_id === system._id)
    );
    const followed_matches = followedSystems.filter(system =>
        game_matches.some(game_match => game_match.system_id === system._id)
    );
    
    // Determine if this is embedded or standalone
    const isEmbedded = props.game !== undefined;
    const isMobile = props.isMobile || false;
    const backToList = props.backToList || (() => {});
    
    // get home and away standings
    const getTeamStandings = (team_id, sport) => {
        if(!team_id || !sport) return null;

        if(sport === "mlb"){
            return sportStandings?.Standings?.find(standing => standing.Key === team_id);
        }else if(sport === "nfl"){
            return sportStandings?.Reg?.find(standing => standing.Team === team_id);
        }else if(sport === "nba"){
            return sportStandings?.Standings?.find(standing => standing.Key === team_id);
        }
    }

    const homeStandings = getTeamStandings(home_team, sport);
    const awayStandings = getTeamStandings(away_team, sport);

    // Get team records for home and away teams
    const getTeamRecord = (standings) => {
        if(!standings) return null;
        const wins = standings?.Wins || 0;
        const losses = standings?.Losses || 0;
        const ties = standings?.Ties || 0;

        if(ties > 0){
            return `(${wins}-${losses}-${ties})`;
        }else{
            return `(${wins}-${losses})`;
        }
    }
    const homeRecord = getTeamRecord(homeStandings);
    const awayRecord = getTeamRecord(awayStandings); 

    // Render tab content based on active tab
    const renderTabContent = () => {
        switch(activeTab) {
            case 'overview':
                return (
                    <div className="tab-content m-2">
                        {/* Game Overview Info */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                            {/* Game Information - Full Width */}
                            <div className="col-span-full relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6">
                                {/* Game Analyzer Button - Top Right */}
                                {['mlb', 'nba', 'nfl'].includes(sport) && isEmbedded && (
                                    <div className="absolute top-4 right-4 hidden sm:block">
                                        <PrimaryBtn
                                            onClick={() => navigate('/game-analyzer', { state: { game: game, sport: sport } })}
                                            title="Open Game Analyzer"
                                        >
                                            Game Analyzer
                                        </PrimaryBtn>
                                    </div>
                                )}
                                {/* Top row - Game Matchup */}
                                <div className="flex items-center justify-center md:justify-start lg:justify-center space-x-8 mb-4">
                                    <div className="text-center">
                                        <div className="text-center text-xl font-bold text-gray-900 dark:text-white">{awayTeamLabel}</div>
                                        <div className="text-sm text-gray-600 dark:text-gray-400">{awayRecord}</div>
                                    </div>
                                    <span className="text-xl text-gray-900 dark:text-white">@</span>
                                    <div className="text-center">
                                        <div className="text-center text-xl font-bold text-gray-900 dark:text-white">{homeTeamLabel}</div>
                                        <div className="text-sm text-gray-600 dark:text-gray-400">{homeRecord}</div>
                                    </div>
                                </div>
                                
                                {/* Horizontal divider */}
                                <div className="w-full lg:w-1/2 mx-auto h-px bg-gray-300 dark:bg-gray-600 my-4"></div>
                                
                                {/* Bottom row - Venue and Sport with labels */}
                                <div className="flex items-center justify-center space-x-8">
                                    {game?.stadium?.Name && (
                                        <div className="text-center">
                                            <div className="text-xs sm:text-sm text-gray-900 dark:text-white">
                                                {game.stadium.Name}
                                            </div>
                                        </div>
                                    )}

                                    {/* Vertical divider */}
                                    <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 my-4"></div>

                                    <div className="text-center">
                                        <div className="text-xs sm:text-sm text-gray-900 dark:text-white">
                                            {sportLabel}
                                        </div>
                                    </div>

                                    {/* Vertical divider */}
                                    <div className="w-px h-5 bg-gray-300 dark:bg-gray-600 my-4"></div>

                                    <div className="text-center">
                                        <div className="text-xs sm:text-sm text-gray-900 dark:text-white">
                                            {date}
                                        </div>
                                        <div className="text-xs sm:text-sm text-gray-900 dark:text-white">
                                            {time}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Weather Data */}
                            <div className="w-full">
                                <WeatherData 
                                    game={game} 
                                    sport={sport} 
                                    stadiumOrientation={
                                        sport === "mlb" && game.stadium_id && mlbStadiumOrientations
                                            ? mlbStadiumOrientations[game.stadium_id] || null
                                        : null
                                    }
                                />
                            </div>

                            {/* Lines Overview */}
                            <div className="w-full">
                                <LinesOverview game={game} sport={sport} homeTeam={homeTeamLabel} awayTeam={awayTeamLabel} />
                            </div>

                            {/* Team Breakdown */}
                            <div className="col-span-full">
                                <TeamBreakdown game={game} sport={sport} homeStandings={homeStandings} awayStandings={awayStandings} homeTeam={homeTeamLabel} awayTeam={awayTeamLabel} />
                            </div>
                        </div>
                    </div>
                );
            case 'stats':
                return (
                    <div className="tab-content m-2">
                        {sport === "mlb" && (
                            <MlbStatAvgs game={game} showCarets={true} />
                        )}
                        {sport === "nfl" && (
                            <NflStatAvgs game={game} showCarets={true} />
                        )}
                        {sport === "nba" && (
                            <NbaStatAvgs game={game} showCarets={true} />
                        )}
                    </div>
                );
                
            case 'betting':
                return (
                    <div className="tab-content m-2">
                        {sport === "mlb" && (
                            <MlbBettingData game={game} />
                        )}
                        {["nfl", "nba"].includes(sport) && (
                            <SportBettingData game={game} />
                        )}
                    </div>
                );
                
            case 'systems':
                return (
                    <div className="tab-content">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Matching Systems</h3>
                        {priv_matches.length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-3">Your Systems</h4>
                                <SystemsGrid 
                                    systems={priv_matches} 
                                    sysType="private"
                                />
                            </div>
                        )}
                        
                        {followed_matches.length > 0 && (
                            <div className="mb-6">
                                <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-3">Followed Systems</h4>
                                <SystemsGrid 
                                    systems={followed_matches} 
                                    sysType="followed"
                                />
                            </div>
                        )}
                        
                        {followed_matches.length === 0 && priv_matches.length === 0 && (
                            <p className="text-center text-gray-500 dark:text-gray-400 py-8">No matching systems</p>
                        )}
                    </div>
                );
                
            default:
                return null;
        }
    };
    
    const gameContent = (
        <div className="w-full">
            {/* Header Section */}
            <div className="flex flex-row gap-2 justify-between mb-4 md:mb-0 align-middle">
                {isMobile && isEmbedded && (
                    <button 
                        onClick={backToList}
                        className="inline-flex items-center text-brand-red-500 hover:text-brand-red-600 dark:text-white font-semibold text-sm"
                    >
                        <ArrowLeftCircleIcon className="h-5 mr-1"/> Back
                    </button>
                )}
                {['mlb', 'nba', 'nfl'].includes(sport) && isEmbedded && (
                    <div className="sm:hidden">
                        <PrimaryBtn
                            onClick={() => navigate('/game-analyzer', { state: { game: game, sport: sport } })}
                            title="Open Game Analyzer"
                            className="h-8"
                        >
                            Game Analyzer
                        </PrimaryBtn>
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="flex md:justify-start overflow-x-auto border-y border-gray-200 dark:border-gray-700">
                <button 
                    className={`flex items-center px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                        activeTab === 'overview' 
                            ? 'text-brand-red-500 border-brand-red-500' 
                            : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-white'
                    }`}
                    onClick={() => setActiveTab('overview')}
                >
                    <svg className={`w-4 h-4 mr-2 ${
                        activeTab === 'overview'
                            ? 'fill-brand-red-500'
                            : 'fill-gray-600 dark:fill-gray-400'
                    }`} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <span>Overview</span>
                </button>
                <button 
                    className={`flex items-center px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                        activeTab === 'stats' 
                            ? 'text-brand-red-500 border-brand-red-500' 
                            : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-white'
                    }`}
                    onClick={() => setActiveTab('stats')}
                >
                    <StatsIcon className={`w-4 h-4 mr-2 ${
                        activeTab === 'stats'
                            ? 'fill-brand-red-500'
                            : 'fill-gray-600 dark:fill-gray-400'
                    }`} />
                    <span>Stats</span>
                </button>
                <button 
                    className={`flex items-center px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                        activeTab === 'betting' 
                            ? 'text-brand-red-500 border-brand-red-500' 
                            : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-white'
                    }`}
                    onClick={() => setActiveTab('betting')}
                >
                    <BettingIcon className={`w-4 h-4 mr-2 ${
                        activeTab === 'betting'
                            ? 'fill-brand-red-500'
                            : 'fill-gray-600 dark:fill-gray-400'
                    }`} />
                    <span>Lines</span>
                </button>
                <button 
                    className={`flex items-center px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                        activeTab === 'systems' 
                            ? 'text-brand-red-500 border-brand-red-500' 
                            : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-white'
                    }`}
                    onClick={() => setActiveTab('systems')}
                >
                    <SystemsIcon className={`w-4 h-4 mr-2 ${
                        activeTab === 'systems'
                            ? 'fill-brand-red-500'
                            : 'fill-gray-600 dark:fill-gray-400'
                    }`} />
                    <span>Systems</span>
                </button>
            </div>

            {/* Tab Content */}
            <div className="overflow-auto">
                {renderTabContent()}
            </div>
        </div>
    );
    
    // If the component is embedded in the dashboard, render just the content
    if (isEmbedded) {
        return gameContent;
    }
    
    // Otherwise, render the full page layout
    return(
        <div className="min-h-screen bg-white dark:bg-brand-dark-900">
            <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
                {gameContent}
            </div>
        </div>
    )
}

export default AuthProtected(UpcomingGame);